import React from "react";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getI18n: (i18nData: any, key: string, options?: any) => string;
  }
}

interface TranscribedFile {
  id: string;
  filename: string;
  type: "audio" | "video";
  date: string;
  language: string;
  duration: string;
  status: TranscribedFileStatus;
}

interface TranscribedFileTableProps {
  files: TranscribedFile[];
  onEdit: (fileId: string) => void;
  loading?: boolean;
  ti18n?: Record<string, string>;
  hideHeader?: boolean;
}

const t = (ti18n: Record<string, string> | undefined, key: string): string => {
  if (ti18n) {
    return window.getI18n(ti18n, key);
  }
  return key;
};

const ACTIVE_COLOR = "#EC7F54";

const TRANSCRIBED_FILE_STATUS = {
  COMPLETED: "Completed",
  FAILED: "Failed",
  INITIALIZED: "Initialized",
} as const;

type TranscribedFileStatus = typeof TRANSCRIBED_FILE_STATUS[keyof typeof TRANSCRIBED_FILE_STATUS];

const STATUS_COLORS: Partial<Record<TranscribedFileStatus, string>> = {
  [TRANSCRIBED_FILE_STATUS.FAILED]: "var(--trebble-error-color, #e74c3c)",
  [TRANSCRIBED_FILE_STATUS.INITIALIZED]: ACTIVE_COLOR,
};

const AudioIcon: React.FC = () => (
  <svg width={20} height={20} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6v4h3l4 3V3L6 6H3z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M12.5 5.5a4 4 0 010 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const VideoIcon: React.FC = () => (
  <svg width={20} height={20} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1.5" y="3.5" width="9" height="9" stroke="currentColor" strokeWidth="1.2" />
    <path d="M10.5 6l4-2v8l-4-2V6z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

const ROW_CLASSES =
  "flex items-center gap-3 px-4 py-3.5 border-b border-solid text-base";
const ROW_INLINE_STYLE: React.CSSProperties = {
  borderBottomColor: "var(--border-default-color, #eee)",
  color: "var(--text-default-color)",
  fontSize: "var(--text-base)",
};
const HEADER_INLINE_STYLE: React.CSSProperties = {
  ...ROW_INLINE_STYLE,
  borderBottomWidth: "2px",
};

const TranscribedFileTable: React.FC<TranscribedFileTableProps> = ({
  files,
  onEdit,
  loading = false,
  ti18n,
  hideHeader = false,
}) => {
  return (
    <div style={{ backgroundColor: "var(--bg-default)" }}>
      {/* Header */}
      {!hideHeader && <div className={`${ROW_CLASSES} font-bold`} style={HEADER_INLINE_STYLE}>
        <div style={{ width: 36, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>{t(ti18n, "NAME")}</div>
        <div style={{ width: 140, flexShrink: 0 }}>{t(ti18n, "DATE")}</div>
        <div style={{ width: 100, flexShrink: 0 }}>{t(ti18n, "LANGUAGE")}</div>
        <div style={{ width: 80, flexShrink: 0 }}>{t(ti18n, "DURATION")}</div>
        <div style={{ width: 100, flexShrink: 0 }}>{t(ti18n, "STATUS")}</div>
        <div style={{ width: 80, flexShrink: 0 }} />
      </div>}

      {/* Loading state */}
      {loading && (
        <div className={ROW_CLASSES} style={ROW_INLINE_STYLE}>
          <div className="w-full text-center" style={{ color: "var(--text-muted)" }}>
            {t(ti18n, "PROCESSING")}…
          </div>
        </div>
      )}

      {/* Rows — skip while loading to avoid showing stale data */}
      {!loading && files.map((file) => {
        const canEdit = file.status === TRANSCRIBED_FILE_STATUS.COMPLETED;
        return (
          <div key={file.id} className={ROW_CLASSES} style={ROW_INLINE_STYLE}>
            {/* Icon */}
            <div style={{ width: 36, flexShrink: 0, display: "flex", justifyContent: "center", color: "var(--text-muted)" }}>
              {file.type === "audio" ? <AudioIcon /> : <VideoIcon />}
            </div>

            {/* Name */}
            <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {file.filename}
            </div>

            {/* Date */}
            <div style={{ width: 140, flexShrink: 0, color: "var(--text-default-color)" }}>
              {file.date}
            </div>

            {/* Language */}
            <div style={{ width: 100, flexShrink: 0, color: "var(--text-default-color)" }}>
              {file.language}
            </div>

            {/* Duration */}
            <div style={{ width: 80, flexShrink: 0, color: "var(--text-default-color)" }}>
              {file.duration}
            </div>

            {/* Status */}
            <div style={{
              width: 100,
              flexShrink: 0,
              color: STATUS_COLORS[file.status] || "var(--text-default-color)",
            }}>
              {t(ti18n, file.status.toUpperCase())}
            </div>

            {/* Action */}
            <div style={{ width: 80, flexShrink: 0 }}>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(file.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    border: "none",
                    background: "none",
                    color: ACTIVE_COLOR,
                    fontWeight: 700,
                    fontSize: "var(--text-base)",
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "inherit",
                  }}
                >
                  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 14h3l8-8-3-3-8 8v3z" stroke={ACTIVE_COLOR} strokeWidth="1.2" strokeLinejoin="round" />
                    <path d="M10 3l3 3" stroke={ACTIVE_COLOR} strokeWidth="1.2" />
                  </svg>
                  {t(ti18n, "EDIT")}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TranscribedFileTable;
